/**
 * The TypeSafe adapter: one request shape, one answer shape, no SDK.
 *
 * A judge request is a JSON body — a state and named typed questions — and its
 * answer is a JSON body of typed answers. That is the whole protocol, and it is
 * small enough that a fetch and a decoder are the honest implementation: the
 * official SDK would add a dependency to the boot module graph for a client that
 * this file reimplements in a screen, and every provider SDK this app does carry
 * is loaded on demand for exactly that reason.
 *
 * What this file is careful about, in order of how much it would cost to get
 * wrong:
 *
 *  - It never invents an answer. A body that does not decode — an answer of the
 *    wrong type, a probability outside the unit interval, a choice naming a label
 *    the question did not offer — fails the whole call rather than being repaired.
 *    A judge whose answers can be patched into shape is a judge nobody can
 *    calibrate.
 *  - It mints the stamp from the RESPONSE's model id, never the request's. The
 *    alias `jev-latest` resolves server-side to a dated version, and the stamp
 *    names that version because it names what answered (invariant 3).
 *  - It classifies failures into the same closed `ModelError` kinds the passes
 *    use, so a caller and the run loop read a judge failure the way they read
 *    any other: a rejected key is `auth`, an exhausted account is `quota`, and
 *    a deadline is `timeout` rather than `aborted`.
 *  - It retries only what a retry can fix — a rate limit, a server error, a
 *    connection that dropped — and every attempt is counted on the result, so an
 *    evaluation that wants no invisible retries can ask for none and see that it
 *    got none.
 */

import { judgeStampFor } from '../../core/models'
import type { JudgeEntry } from '../../core/models'
import type { Spend } from '../../core/types'
import { ModelError } from './contract'
import { cspRefused, cspRefusedSentence } from './cspRefused'
import type { JudgeAnswer, JudgeJson, JudgeQuestion, JudgeResult } from '../judge'

/** Where a request goes when nothing else is set. Overridable only by a test or a
 *  proxy the user runs — the registry's `host` is what every surface names. */
export const TYPESAFE_BASE_URL = 'https://api.typesafe.ai'
export const SYSTEM_ONE_PATH = '/v1/systemone'

/**
 * How long one attempt may take before it is presumed hung.
 *
 * Twenty seconds against a service whose published end-to-end latency is under
 * a second. A pass deadline of ten minutes exists because a reasoning model can
 * honestly think for that long; a judge that has not answered in twenty seconds
 * is not thinking, it is stuck, and a run waiting on it is a run not checking
 * anything.
 */
export const JUDGE_TIMEOUT_MS = 20_000

/** Retries after the first attempt, on the retryable kinds only. */
export const JUDGE_RETRIES = 2

const BACKOFF_INITIAL_MS = 500
const BACKOFF_MAX_MS = 5_000

export interface TypeSafeCall {
  entry: JudgeEntry
  apiKey: string
  baseURL?: string
  state: JudgeJson
  questions: Record<string, JudgeQuestion>
  signal?: AbortSignal
  timeoutMs?: number
  maxRetries?: number
  /** Replaceable for tests and for a proxy. Defaults to the global `fetch`. */
  fetch?: typeof globalThis.fetch
  /** Replaceable for tests, so a retry's wait is not a real wait. */
  sleep?: (ms: number) => Promise<void>
}

/** Sent as the wire `type` tag. The SDKs name the boolean primitive `noul`. */
const wireQuestion = (q: JudgeQuestion): JudgeJson => {
  switch (q.type) {
    case 'noul':
      return {
        type: 'noul',
        instructions: q.instructions,
        ...(q.criteria ? { criteria: q.criteria } : {}),
      }
    case 'choice':
      return { type: 'choice', instructions: q.instructions, criteria: q.criteria }
    case 'score':
      return { type: 'score', instructions: q.instructions, criteria: [...q.criteria] }
  }
}

const unit = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1

const probabilities = (v: unknown, keys: readonly string[]): Record<string, number> | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null
  const o = v as Record<string, unknown>
  const out: Record<string, number> = {}
  for (const k of keys) {
    const p = o[k]
    if (!unit(p)) return null
    out[k] = p
  }
  return out
}

/**
 * One answer, decoded against the question it answers, or null.
 *
 * Null is the whole call failing, and that is deliberate — see the header. The
 * checks are the contract the SDKs' own decoders enforce, plus one they leave to
 * the caller: a choice must name one of the labels the question offered, because
 * a label the question never offered is not a selection, it is a hallucination
 * in the one channel that promises none.
 */
export function decodeAnswer(q: JudgeQuestion, v: unknown): JudgeAnswer | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  if (o.type !== q.type) return null
  if (q.type === 'noul') return unit(o.noul) ? { type: 'noul', noul: o.noul } : null
  if (q.type === 'choice') {
    const labels = Object.keys(q.criteria)
    if (typeof o.choice !== 'string' || !labels.includes(o.choice) || !unit(o.confidence)) {
      return null
    }
    const p = probabilities(o.probabilities, labels)
    return p
      ? { type: 'choice', choice: o.choice, confidence: o.confidence, probabilities: p }
      : null
  }
  const levels = q.criteria.map((_, i) => String(i))
  if (typeof o.score !== 'number' || !Number.isFinite(o.score) || !unit(o.confidence)) return null
  if (o.score < 0 || o.score > q.criteria.length - 1) return null
  const p = probabilities(o.probabilities, levels)
  return p ? { type: 'score', score: o.score, confidence: o.confidence, probabilities: p } : null
}

/** Decode a whole body against the questions that were sent, or throw. */
export function decodeBody(
  entry: JudgeEntry,
  questions: Record<string, JudgeQuestion>,
  body: unknown,
): {
  model: string
  answers: Record<string, JudgeAnswer>
  inputTokens: number
  outputTokens: number
} {
  const o = (body ?? {}) as Record<string, unknown>
  const model = typeof o.model === 'string' && o.model.trim() ? o.model.trim() : entry.id
  const raw = (o.answers ?? {}) as Record<string, unknown>
  const answers: Record<string, JudgeAnswer> = {}
  for (const [name, q] of Object.entries(questions)) {
    const a = decodeAnswer(q, raw[name])
    if (!a) {
      throw new ModelError(
        `The judge answered “${name}” in a shape this app cannot read, so nothing from this call was kept.`,
        'unknown',
      )
    }
    answers[name] = a
  }
  const usage = (o.usage ?? {}) as Record<string, unknown>
  const count = (v: unknown): number =>
    typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.trunc(v) : 0
  return {
    model,
    answers,
    inputTokens: count(usage.input_tokens),
    outputTokens: count(usage.output_tokens),
  }
}

/** The `ModelError` kind for an HTTP status the judge answered with. */
export function kindOfStatus(status: number): ModelError['kind'] {
  if (status === 401 || status === 403) return 'auth'
  if (status === 402) return 'quota'
  if (status === 408 || status === 429) return 'rate'
  if (status >= 500) return 'server'
  if (status >= 400) return 'request'
  return 'unknown'
}

const RETRYABLE = new Set<ModelError['kind']>(['rate', 'server', 'network'])

/** `retry-after-ms` in milliseconds, else `Retry-After` in seconds, else null. */
export function retryAfterMs(headers: Headers): number | null {
  const ms = Number(headers.get('retry-after-ms'))
  if (Number.isFinite(ms) && ms > 0) return ms
  const s = Number(headers.get('retry-after'))
  if (Number.isFinite(s) && s > 0) return s * 1000
  return null
}

/** A short, readable line from an error body, or nothing. */
const detailOf = (text: string): string => {
  try {
    const o = JSON.parse(text) as Record<string, unknown>
    const d = o.detail ?? o.error ?? o.message
    if (typeof d === 'string') return d
    if (d && typeof d === 'object' && typeof (d as { message?: unknown }).message === 'string') {
      return (d as { message: string }).message
    }
  } catch {
    // Not JSON. The raw text is fine at this length.
  }
  return text.slice(0, 200)
}

const defaultSleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms))

/**
 * One judge call, with its retries, its deadline and its decode.
 *
 * The result carries `attempts` so a caller that asked for no retries can see it
 * got none, and a caller that allowed them can see how many the answer took.
 */
export async function runTypeSafe(o: TypeSafeCall): Promise<JudgeResult> {
  const fetchImpl = o.fetch ?? globalThis.fetch
  const sleep = o.sleep ?? defaultSleep
  const url = `${(o.baseURL ?? TYPESAFE_BASE_URL).replace(/\/+$/, '')}${SYSTEM_ONE_PATH}`
  // Before the first attempt, because a key that cannot travel in a header
  // fails inside `fetch` with "Cannot convert argument to a ByteString", which
  // the network branch below would then report as the host being unreachable.
  // It happened: a command pasted with the placeholder ellipsis still in it
  // sent `Bearer …` and read as api.typesafe.ai refusing the connection. A key
  // is printable ASCII or it is not a key, and the sentence says which
  // character broke it without printing the rest.
  const bad = /[^\x21-\x7e]/.exec(o.apiKey)
  if (bad) {
    throw new ModelError(
      `The TypeSafe key holds a character that cannot be sent in a header (${JSON.stringify(bad[0])} at position ${bad.index}): it was pasted with a placeholder, a space or a line break in it. Paste the key itself.`,
      'auth',
    )
  }
  const timeoutMs = o.timeoutMs ?? JUDGE_TIMEOUT_MS
  const maxRetries = Math.max(0, o.maxRetries ?? JUDGE_RETRIES)
  const wire: Record<string, JudgeJson> = {}
  for (const [name, q] of Object.entries(o.questions)) wire[name] = wireQuestion(q)
  const body = JSON.stringify({ model: o.entry.id, state: o.state, questions: wire })
  const startedAt = Date.now()

  let attempts = 0
  for (;;) {
    attempts++
    if (o.signal?.aborted) throw new ModelError('Stopped.', 'aborted')
    const controller = new AbortController()
    let timedOut = false
    const forward = () => controller.abort()
    o.signal?.addEventListener('abort', forward, { once: true })
    const timer = setTimeout(() => {
      timedOut = true
      controller.abort()
    }, timeoutMs)
    let failure: ModelError
    try {
      let res: Response
      try {
        res = await fetchImpl(url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${o.apiKey}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body,
          signal: controller.signal,
        })
      } catch (err) {
        if (o.signal?.aborted) throw new ModelError('Stopped.', 'aborted')
        if (timedOut) {
          throw new ModelError(
            `The judge did not answer within ${Math.round(timeoutMs / 1000)} seconds, so the question was abandoned rather than left hanging.`,
            'timeout',
          )
        }
        // A fetch that throws never reached a response: DNS, TLS, a dropped
        // connection — or, from a browser, an origin the API does not allow, which
        // arrives as the same TypeError and is named in the sentence for it. One
        // the page's own policy refused is named for what it was (D336).
        const host = new URL(url).host
        throw new ModelError(
          cspRefused(host)
            ? cspRefusedSentence(host)
            : `Could not reach ${host}: ${err instanceof Error ? err.message : String(err)}. From a browser this is also what a refused cross-origin request looks like.`,
          'network',
        )
      }
      const text = await res.text()
      if (res.ok) {
        const decoded = decodeBody(o.entry, o.questions, text ? JSON.parse(text) : {})
        const model = judgeStampFor(o.entry, decoded.model)
        const spend: Spend = {
          model,
          inputTokens: decoded.inputTokens,
          outputTokens: decoded.outputTokens,
          searches: 0,
          // Every input token bills at base: the judge has no cache to read
          // from or write to, so the breakdown is the aggregate under one name.
          detail: {
            inputUncached: decoded.inputTokens,
            cacheWrite5m: 0,
            cacheWrite30m: 0,
            cacheWrite1h: 0,
            cacheRead: 0,
            inputUnclassified: 0,
            webSearches: 0,
            webFetches: 0,
            serverRequestsUnclassified: 0,
          },
        }
        return {
          model,
          answers: decoded.answers,
          spend,
          requestId: res.headers.get('x-typesafe-request-id'),
          attempts,
          startedAt,
          completedAt: Date.now(),
        }
      }
      const kind = kindOfStatus(res.status)
      // The status and the server's own words travel on every kind, the auth
      // kinds included: a 401 for a key that is wrong and a 403 for a key that
      // is right but not allowed to are both "rejected", and the sentence that
      // only said so sent one reader to re-paste a key the service had accepted.
      const detail = detailOf(text)
      const said = `TypeSafe answered ${res.status}${detail ? `: ${detail}` : ''}`
      failure = new ModelError(
        kind === 'auth'
          ? `TypeSafe rejected the key (${said}). A 401 is a key it does not recognise; a 403 is one it does but will not serve this with.`
          : kind === 'quota'
            ? `The TypeSafe account has no credit left for this (${said}).`
            : `${said}.`,
        kind,
      )
      const wait = retryAfterMs(res.headers)
      if (RETRYABLE.has(kind) && attempts <= maxRetries) {
        await sleep(Math.min(wait ?? backoff(attempts), BACKOFF_MAX_MS * 12))
        continue
      }
    } catch (err) {
      if (!(err instanceof ModelError)) throw err
      if (!RETRYABLE.has(err.kind) || attempts > maxRetries) throw err
      failure = err
      await sleep(backoff(attempts))
      continue
    } finally {
      clearTimeout(timer)
      o.signal?.removeEventListener('abort', forward)
    }
    throw failure
  }
}

/** Exponential, from half a second, capped, with a quarter of jitter taken off. */
const backoff = (attempt: number): number => {
  const base = Math.min(BACKOFF_INITIAL_MS * 2 ** (attempt - 1), BACKOFF_MAX_MS)
  return Math.round(base * (1 - Math.random() * 0.25))
}
